import { API_URL } from "../config/api";
import { QUESTS_API_URL } from "../config/questsApi";

const LEGACY_AUTH_STORAGE_KEY = "vocabularium_auth";
const AUTH_CHANNEL_NAME = "vocabularium-auth";

let refreshPromise = null;
let fetchPatched = false;
let currentAuthEmail = "";
let authChannel = null;
let sessionActive = false;

export function markSessionActive(active) {
  sessionActive = Boolean(active);
}

export function purgeLegacyTokenStorage() {
  try {
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
    sessionStorage.removeItem(LEGACY_AUTH_STORAGE_KEY);
  } catch {
    // quota / mode privé
  }
}

export function rememberAuthEmail(email) {
  currentAuthEmail = String(email || "").trim();
}

export function clearAuthSession() {
  currentAuthEmail = "";
  sessionActive = false;
  purgeLegacyTokenStorage();
}

function getAuthChannel() {
  if (typeof window === "undefined" || typeof BroadcastChannel === "undefined") {
    return null;
  }
  if (!authChannel) {
    try {
      authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    } catch {
      authChannel = null;
    }
  }
  return authChannel;
}

export function notifyAuthChanged() {
  try {
    getAuthChannel()?.postMessage({ type: "changed" });
  } catch {
    // ignore
  }
}

export function subscribeAuthChanged(handler) {
  const channel = getAuthChannel();
  if (!channel) return () => {};
  const onMessage = () => handler();
  channel.addEventListener("message", onMessage);
  return () => {
    channel.removeEventListener("message", onMessage);
  };
}

export async function fetchServerAuthSession() {
  try {
    const res = await fetch(`${API_URL}/auth/session`, {
      credentials: "include",
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.sub) return null;
    if (json.challenge === "DISPLAY_NAME_REQUIRED") {
      rememberAuthEmail(json.email || "");
      return json;
    }
    rememberAuthEmail(json.email || "");
    markSessionActive(true);
    return json;
  } catch {
    return null;
  }
}

async function refreshAuthSession() {
  if (!refreshPromise) {
    refreshPromise = fetch(`${API_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: "{}",
    })
      .then(async (res) => {
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
          clearAuthSession();
          return null;
        }
        rememberAuthEmail(json.email || currentAuthEmail);
        return json;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function isVocabulariumApiUrl(url) {
  const value = String(url || "");
  if (value.startsWith("/apivocabularium")) return true;
  if (API_URL && value.startsWith(API_URL)) return true;
  if (QUESTS_API_URL && value.startsWith(QUESTS_API_URL)) return true;
  return false;
}

function shouldRefreshOn401(url) {
  if (!isVocabulariumApiUrl(url)) return false;
  const value = String(url || "");
  return (
    !value.includes("/auth/refresh") &&
    !value.includes("/auth/login") &&
    !value.includes("/auth/logout") &&
    !value.includes("/auth/session")
  );
}

function withCredentials(init, url) {
  if (!isVocabulariumApiUrl(url)) return init;
  if (init.credentials != null) return init;
  return { ...init, credentials: "include" };
}

export function installAuthFetch() {
  if (fetchPatched || typeof window === "undefined") return;
  fetchPatched = true;
  purgeLegacyTokenStorage();
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : input?.url;
    const headers = new Headers(
      init.headers || (typeof input !== "string" ? input?.headers : undefined) || {}
    );
    const nextInit = withCredentials({ ...init, headers }, url);

    if (
      QUESTS_API_URL &&
      String(url || "").startsWith(QUESTS_API_URL) &&
      !headers.has("X-Vocabularium-Email") &&
      currentAuthEmail
    ) {
      headers.set("X-Vocabularium-Email", currentAuthEmail);
    }

    const response = await originalFetch(input, nextInit);
    if (response.status !== 401 || !shouldRefreshOn401(url)) {
      return response;
    }

    const refreshed = await refreshAuthSession();
    if (!refreshed) {
      if (sessionActive) {
        markSessionActive(false);
        window.dispatchEvent(new Event("vocabularium-auth-expired"));
      }
      return response;
    }

    return originalFetch(input, withCredentials({ ...init, headers }, url));
  };
}
